/**
 * Token di design della dashboard.
 *
 * Unica fonte di verità per colori, tipografia, spaziature e densità. Il file
 * non importa React: espone solo dati, che `InstitorConfigProvider` passa al
 * `ConfigProvider` di antd. Nessun componente scrive un colore o una misura:
 * chi ha bisogno di un valore lo legge con `theme.useToken()`, o da qui quando
 * il valore non passa dagli algoritmi (le due tinte del marchio).
 *
 * Le scale seguono le regole di Ant Design:
 *
 *  - **spaziature** `y = 8 + 8n`, con i tre formati canonici 8 / 16 / 24;
 *    `sizeUnit` e `sizeStep` restano a 4, che è il passo della libreria;
 *  - **tipografia** con base 14 e titoli 38 / 30 / 24 / 20 / 16, cioè la scala
 *    di antd tenuta esplicita invece che ereditata in silenzio. L'unico
 *    scostamento è `fontSizeHeading1`, portato a 30: in un cruscotto che ha già
 *    il marchio nell'intestazione, 38 px di titolo di pagina sono sproporzionati;
 *  - **colore** con i quattro ruoli semantici dichiarati (primario, riuscita,
 *    avviso, errore), perché un componente non debba mai scegliere una tinta.
 *
 * ## Token comuni e token per tema
 *
 * Dal 2026-09-20 il file è diviso in tre insiemi, e la divisione ha una regola
 * precisa: **un token sta in `commonTokens` se non è un colore**, e sta in
 * `lightTokens` / `darkTokens` solo se il tema ne cambia il valore.
 *
 * Misure, raggi, famiglie tipografiche e spaziature non dipendono dal tema: un
 * padding non diventa più largo al buio. I colori invece dipendono quasi tutti,
 * ma **non tutti allo stesso modo**: superfici, bordi e testi li derivano gli
 * algoritmi di antd (`defaultAlgorithm` e `darkAlgorithm`) e qui si tocca solo
 * ciò che l'algoritmo porta sotto soglia di contrasto. I semi semantici invece
 * si dichiarano per tema, perché la modalità scura **non è un'inversione**:
 * un blu che sul bianco è leggibile, su un fondo quasi nero non lo è.
 *
 * ## Stile Fondly + ChatMind (ADR 0021)
 *
 * Dal 2026-09-29 la dashboard unisce i due template (dal 2026-09-30 si chiamano ZakatWise, oggi Fondly,
 * `templates/fondly-web`, e ChatMind, `templates/chatmind-mobile`): da **Fondly** il verde petrolio come
 * colore d'accento (link, voce attiva, schede, interruttori), la barra laterale con la card
 * «Nuovo set», angoli morbidi; da **ChatMind** (lo stesso stile dell'app mobile, ADR 0020) il
 * nero d'inchiostro `#141718` del marchio e dei pulsanti principali, i fondi `#F7F7F8`, Poppins
 * per i titoli. Il testo resta in Inter. I pulsanti principali prendono l'inchiostro con i token
 * di componente (`componentTokensFor`), il resto dell'interfaccia usa il verde del primario.
 *
 * Ogni valore che si scosta dal default di antd porta accanto il rapporto di
 * contrasto misurato che lo giustifica. I numeri non sono stimati: li calcola
 * `contrast.test.ts` sui token risolti da `theme.getDesignToken`, e se un
 * ritocco li fa scendere sotto soglia la suite diventa rossa.
 */

import type { ThemeConfig } from 'antd';

import type { ResolvedTheme } from './themeMode';

/** Scala delle spaziature, in pixel. Passo 4, formati canonici 8 / 16 / 24. */
export const spacing = {
  /** Spazio minimo: distanza fra un'icona e la sua etichetta. */
  xxs: 4,
  /** Spazio piccolo: fra controlli della stessa riga. */
  xs: 8,
  /** Spazio intermedio: fra campi di un form. */
  sm: 12,
  /** Spazio normale: padding interno di card e celle. */
  md: 16,
  /** Spazio grande: fra sezioni della stessa pagina. */
  lg: 24,
  /** Spazio massimo: fra blocchi indipendenti. */
  xl: 32,
} as const;

/**
 * Fondo del marchio: intestazione, barra laterale e cassetto della navigazione.
 *
 * È l'unico colore che **non** dipende dal tema, e di proposito: un blu notte
 * è già una superficie scura, quindi funziona sotto entrambi gli algoritmi, e
 * tenerlo fisso evita che il marchio cambi identità quando cambia il tema.
 * Dal 2026-09-29 (ADR 0021) è l'inchiostro di ChatMind, lo stesso dell'app mobile e della sua
 * icona. Contrasto con
 * `brandOnSurface` misurato da `contrast.test.ts` (soglia 4,5:1).
 */
export const brandSurface = '#141718';

/**
 * Testo e icone sopra `brandSurface`.
 *
 * Bianco a opacità 0,92 invece che pieno: il bianco pieno su un fondo così
 * scuro produce alone, e 0,92 lascia comunque 15,7:1.
 */
export const brandOnSurface = 'rgba(255, 255, 255, 0.92)';

/**
 * Token indipendenti dal tema: misure, tipografia, spaziature.
 *
 * Non contengono colori. Se un giorno ne servisse uno qui dentro, vuol dire che
 * o è davvero uguale nei due temi (allora è il caso del marchio, e sta a
 * parte con la sua motivazione) o va spostato negli insiemi per tema.
 */
export const commonTokens = {
  /** Raggio degli angoli: 10 dal 2026-09-29 (ADR 0021), fra i 14 di ChatMind e i campi compatti. */
  borderRadius: 10,
  /** Raggio di card e cassetti: 16, le schede morbide di Fondly. */
  borderRadiusLG: 16,

  /**
   * Famiglia tipografica del testo: Inter (Fondly), dentro il pacchetto con `@fontsource`
   * (nessuna richiesta a CDN, la CSP resta `font-src 'self'`). I titoli usano Poppins, da
   * `styles.css`.
   */
  fontFamily:
    "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  /** Famiglia a spaziatura fissa per slug e identificativi. */
  fontFamilyCode: "ui-monospace, SFMono-Regular, 'Cascadia Mono', Consolas, monospace",

  /** Corpo del testo. */
  fontSize: 14,
  /** Corpo ridotto: testi di aiuto e didascalie. */
  fontSizeSM: 12,
  /** Corpo maggiorato: testo introduttivo di una sezione. */
  fontSizeLG: 16,
  /** Titolo di pagina. Più basso del default 38 di antd, vedi il commento in testa. */
  fontSizeHeading1: 30,
  /** Titolo di sezione. */
  fontSizeHeading2: 24,
  /** Titolo di sottosezione. */
  fontSizeHeading3: 20,
  /** Titolo minore. */
  fontSizeHeading4: 16,
  /** Titolo minimo, pari al corpo. */
  fontSizeHeading5: 14,
  /** Interlinea del corpo: (14 + 8) / 14, la regola di antd. */
  lineHeight: 1.5714285714285714,

  /** Passo base delle dimensioni di antd. */
  sizeUnit: 4,
  /** Incremento fra un formato e il successivo. */
  sizeStep: 4,
  /** Padding interno normale. */
  padding: spacing.md,
  /** Padding interno ridotto. */
  paddingSM: spacing.sm,
  /** Padding interno minimo. */
  paddingXS: spacing.xs,
  /** Padding interno ampio. */
  paddingLG: spacing.lg,
  /** Margine normale. */
  margin: spacing.md,
  /** Margine ridotto. */
  marginSM: spacing.sm,
  /** Margine minimo. */
  marginXS: spacing.xs,
  /** Margine ampio. */
  marginLG: spacing.lg,
} as const;

/**
 * Colori del tema chiaro.
 *
 * I quattro semi semantici sono sobri di proposito: la dashboard è uno
 * strumento di amministrazione e il colore segnala le azioni, non decora.
 * `colorBorder` e `colorTextTertiary` sono gli unici due token derivati che
 * vengono ritoccati, e solo perché i default di antd non arrivano a soglia.
 */
export const lightTokens = {
  /**
   * Colore delle azioni primarie e degli elementi attivi.
   *
   * Verde petrolio di Fondly dal 2026-09-29 (ADR 0021; prima l'indaco `#3b5bdb`): bianco
   * sopra 5,5:1 circa, e come testo o icona sul bianco altrettanto. Il valore esatto lo misura
   * `contrast.test.ts` sui token risolti.
   */
  colorPrimary: '#0f766e',
  /** Colore degli esiti positivi: conferme, stato «in esecuzione». Bianco sopra 5,12:1. */
  colorSuccess: '#227d4b',
  /**
   * Colore degli avvisi: azioni che concedono permessi, stati sospesi.
   *
   * Scurito da `#a86a00` il 2026-09-20: il valore precedente dava 4,44:1 col
   * bianco sopra, appena sotto la soglia 4,5:1. Ora 5,14:1.
   */
  colorWarning: '#9a6100',
  /** Colore degli errori e delle azioni distruttive. Bianco sopra 6,54:1. */
  colorError: '#b3261e',
  /** Colore dei messaggi informativi; allineato al primario per coerenza. */
  colorInfo: '#0f766e',

  /**
   * Fondo della pagina: il grigio chiarissimo di ChatMind. Il bordo dei controlli ci resta
   * sopra i 3:1 (misurato).
   */
  colorBgLayout: '#f7f7f8',

  /**
   * Bordo dei controlli: campi, selettori, pulsanti secondari.
   *
   * Il default di antd (`#d9d9d9`) sta a 1,41:1 sul bianco, cioè molto sotto
   * il 3:1 che WCAG 2.2 chiede al contorno di un componente d'interfaccia: chi
   * ha una vista ridotta non trova il bordo del campo. `#8c8c8c` dà 3,36:1 sul
   * contenitore e 3,08:1 sul fondo della pagina.
   *
   * `colorBorderSecondary` resta quello di antd: separa le righe di una tabella
   * e i settori di una card, cioè è decorazione, che WCAG 2.2 esclude
   * esplicitamente dalla soglia.
   */
  colorBorder: '#8c8c8c',

  /**
   * Terzo livello di testo: segnaposto dei campi, descrizioni degli stati vuoti.
   *
   * Il default di antd è `rgba(0, 0, 0, 0.45)`, cioè 3,35:1 sul bianco: è
   * testo, quindi la soglia è 4,5:1 e non 3:1. A 0,55 sale a 4,74:1.
   */
  colorTextTertiary: 'rgba(0, 0, 0, 0.55)',
} as const;

/**
 * Colori del tema scuro.
 *
 * I semi semantici sono **più chiari** dei corrispondenti del tema chiaro, non
 * gli stessi: `darkAlgorithm` scurisce ulteriormente il seme che riceve, e
 * partendo dai valori del tema chiaro i quattro ruoli finivano fra 2,28:1 e
 * 3,32:1 sul fondo scuro — illeggibili come testo e come icona.
 *
 * Ogni seme è scelto dentro una finestra stretta, e vale la pena scriverne il
 * motivo perché non è ovvio: lo stesso colore fa da **fondo di un pulsante con
 * testo bianco sopra** (serve scuro: luminanza ≤ 0,183) e da **testo o icona sul
 * fondo `#141414`** (serve chiaro: luminanza ≥ 0,120). Le due esigenze sono
 * opposte, e la finestra in cui convivono è l'intervallo fra quei due numeri.
 * Fuori da lì non esiste un valore che soddisfi entrambe: è una proprietà
 * della fisica del contrasto, non una mancanza della palette.
 *
 * Per questo nel tema scuro la soglia del colore semantico **come testo** è 3:1
 * (WCAG 2.2 per gli elementi non testuali: icone, indicatori di stato, bordi
 * colorati) mentre quella del **bianco sopra il colore pieno** resta 4,5:1.
 */
export const darkTokens = {
  /** Azioni primarie, verde petrolio come nel tema chiaro. Soglie misurate da `contrast.test.ts`. */
  colorPrimary: '#158a7f',
  /** Esiti positivi. Bianco sopra 5,09:1; come testo 3,62:1. */
  colorSuccess: '#2a8f57',
  /** Avvisi. Bianco sopra 4,59:1; come testo 4,02:1. */
  colorWarning: '#b57c00',
  /** Errori e azioni distruttive. Bianco sopra 5,02:1; come testo 3,67:1. */
  colorError: '#d9534f',
  /** Messaggi informativi; allineato al primario, come nel tema chiaro. */
  colorInfo: '#158a7f',

  /** Fondo della pagina: l'inchiostro di ChatMind, uguale alla barra laterale. */
  colorBgLayout: '#141718',
  /** Fondo di card e campi: il grigio scuro delle superfici di ChatMind. */
  colorBgContainer: '#1b1e20',
  /** Superfici sollevate (menu, popup): un gradino sopra le card. */
  colorBgElevated: '#232627',

  /**
   * Bordo dei controlli.
   *
   * Il default di `darkAlgorithm` (`#424242`) sta a 1,83:1 sul contenitore.
   * `#707070` dà 3,72:1 sul contenitore, 4,24:1 sul fondo della pagina e
   * 3,33:1 sulle superfici sollevate (menu a tendina, popup).
   */
  colorBorder: '#707070',

  /**
   * Terzo livello di testo.
   *
   * Il default (`rgba(255, 255, 255, 0.45)`) arriva a 4,52:1: passa, ma di due
   * centesimi. A 0,50 sale a 5,30:1, cioè un margine che sopravvive al
   * prossimo ritocco di una superficie.
   */
  colorTextTertiary: 'rgba(255, 255, 255, 0.50)',
} as const;

/**
 * Token globali da passare ad antd per un tema effettivo.
 *
 * @param resolved Tema effettivamente applicato, chiaro o scuro.
 * @returns I token comuni uniti a quelli del tema richiesto, nella forma che
 *   `ConfigProvider` e `theme.getDesignToken` accettano.
 */
export function globalTokensFor(resolved: ResolvedTheme): ThemeConfig['token'] {
  return { ...commonTokens, ...(resolved === 'dark' ? darkTokens : lightTokens) };
}

/**
 * Pulsanti principali con l'inchiostro di ChatMind invece del verde del primario.
 *
 * In chiaro: nero `#141718` con testo bianco (18,1:1). In scuro: quasi bianco con testo
 * inchiostro (16,9:1), come il pulsante principale dell'app mobile. Il bordo dei pulsanti
 * secondari e dei campi resta quello misurato dei token globali.
 */
const buttonTokens = {
  light: { colorPrimary: '#141718', colorPrimaryHover: '#2c3032', colorPrimaryActive: '#000000', primaryShadow: 'none' },
  dark: {
    colorPrimary: '#f2f2f2',
    colorPrimaryHover: '#ffffff',
    colorPrimaryActive: '#d9d9d9',
    primaryColor: '#141718',
    primaryShadow: 'none',
  },
} as const;

/**
 * Token per componente del tema effettivo: la densità di `componentTokens` più i pulsanti.
 *
 * @param resolved Tema effettivamente applicato.
 * @returns I token dei componenti.
 */
export function componentTokensFor(resolved: ResolvedTheme): ThemeConfig['components'] {
  return {
    ...componentTokens,
    // Barra in alto chiara come in Fondly (la barra laterale resta d'inchiostro): stesso
    // fondo delle card del tema, testo principale del tema.
    Layout: { ...componentTokens.Layout, ...headerTokens[resolved] },
    Button: buttonTokens[resolved],
  };
}

/** Barra in alto per tema: fondo delle card e testo principale (ADR 0021). */
const headerTokens = {
  light: { headerBg: '#ffffff', headerColor: 'rgba(0, 0, 0, 0.88)' },
  dark: { headerBg: '#1b1e20', headerColor: 'rgba(255, 255, 255, 0.88)' },
} as const;

/**
 * Token per singolo componente: la densità «cruscotto».
 *
 * I default di antd sono pensati per pagine di contenuto. Qui si stringono le
 * celle della tabella e le voci del menu, e si riduce l'intestazione da 64 a 56
 * px con un padding orizzontale che non spreca larghezza sui telefoni — il
 * default `0 50px` lascia circa 260 px utili su uno schermo da 360.
 *
 * Nessun valore qui dipende dal tema: le due tinte del marchio sono fisse
 * (vedi `brandSurface`), e tutto il resto sono misure.
 */
export const componentTokens = {
  /** Guscio dell'applicazione. */
  Layout: {
    /** Altezza dell'intestazione: 56 px, otto in meno del default. */
    headerHeight: 56,
    /** Padding orizzontale dell'intestazione, ridotto per gli schermi stretti. */
    headerPadding: `0 ${String(spacing.md)}px`,
    /** Fondo dell'intestazione: la superficie del marchio, uguale nei due temi. */
    headerBg: brandSurface,
    /**
     * Testo dell'intestazione.
     *
     * Dichiararlo qui invece che nel foglio di stile evita di ripetere il
     * colore in CSS: il nome del prodotto e il nome utente lo ereditano, e
     * `styles.css` resta senza colori.
     */
    headerColor: brandOnSurface,
    /** Fondo della barra laterale, uguale all'intestazione. */
    siderBg: brandSurface,
    /** Fondo dell'area di contenuto: lasciato al tema, qui solo il piè di pagina. */
    footerPadding: `${String(spacing.md)}px ${String(spacing.lg)}px`,
  },
  /** Menu di navigazione della barra laterale. */
  Menu: {
    /** Altezza di una voce: compatta ma sopra i 24 px di area cliccabile minima. */
    itemHeight: 40,
    /** Raggio della voce selezionata. */
    itemBorderRadius: 6,
    /** Margine verticale fra le voci. */
    itemMarginBlock: 4,
    /** Fondo del menu scuro: l'inchiostro della barra laterale, non il blu notte di antd. */
    darkItemBg: brandSurface,
    /** Fondo dei sottomenu del menu scuro. */
    darkSubMenuItemBg: brandSurface,
    /** Fondo dei menu a comparsa del menu scuro. */
    darkPopupBg: brandSurface,
  },
  /** Tabelle dei dati. */
  Table: {
    /** Padding verticale della cella. */
    cellPaddingBlock: spacing.xs,
    /** Padding orizzontale della cella. */
    cellPaddingInline: spacing.sm,
    /** Padding verticale della cella nel formato piccolo. */
    cellPaddingBlockSM: spacing.xxs,
    /** Peso dell'intestazione di colonna. */
    headerSplitColor: 'transparent',
  },
  /** Schede di una risorsa. */
  Tabs: {
    /** Distanza fra la barra delle schede e il contenuto. */
    horizontalMargin: `0 0 ${String(spacing.lg)}px 0`,
  },
  /** Contenitori. */
  Card: {
    /** Padding interno del corpo. */
    bodyPadding: spacing.lg,
    /** Padding interno dell'intestazione. */
    headerHeight: 48,
  },
  /** Descrizioni chiave-valore della panoramica. */
  Descriptions: {
    /** Padding verticale di una voce. */
    itemPaddingBottom: spacing.sm,
  },
} as const;
